import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Truck, CheckCircle2, Wrench, AlertTriangle, Fuel, Gauge, Plus, Pencil, Trash2, UserCog } from "lucide-react";
import api, { zar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, useFetch, Spinner, StatCard } from "@/components/portal/ui";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const CITIES = ["Cape Town", "Johannesburg", "Durban", "Gqeberha", "Pretoria", "Bloemfontein", "East London", "Gaborone", "Maputo", "Harare", "Lusaka", "Windhoek"];
const WRITE_ROLES = ["super_admin", "managing_director", "operations_manager", "fleet_manager", "finance_manager"];
const money = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`;
const STATUS = (arr) => arr.map((s) => ({ value: s, label: s }));
const efErr = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (!d) return fb;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg}`).join("; ");
  return fb;
};

const VSTATUS = {
  "In Transit": "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  "Available": "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  "Maintenance": "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
  "Loading": "bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300",
};

// Reusable add/edit dialog driven by a field spec
function EntityDialog({ open, setOpen, title, fields, editing, testid, onSave }) {
  const blank = () => Object.fromEntries(fields.map((f) => [f.key, f.default ?? ""]));
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(editing ? Object.fromEntries(fields.map((f) => [f.key, editing[f.key] ?? (f.default ?? "")])) : blank());
  }, [open, editing]); // eslint-disable-line
  const submit = async () => {
    for (const f of fields) if (f.required && !form[f.key] && !(editing && f.createOnly)) return toast.error(`${f.label} is required`);
    const payload = {};
    for (const f of fields) {
      if (editing && f.createOnly) continue;
      const v = form[f.key];
      if (f.type === "number") { if (v === "" || v === null || v === undefined) continue; payload[f.key] = Number(v); }
      else payload[f.key] = v;
    }
    setBusy(true);
    try { await onSave(payload); setOpen(false); }
    catch (e) { toast.error(efErr(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
        <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {title}</DialogTitle><DialogDescription>Fields marked * are required.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {fields.map((f) => (editing && f.createOnly) ? null : (
            <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
              <Label className="text-xs">{f.label}{f.required ? " *" : ""}</Label>
              {f.type === "select" ? (
                <select value={String(form[f.key] ?? "")} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} disabled={editing && f.lockOnEdit} data-testid={`${testid}-f-${f.key}`}
                  className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3 text-sm disabled:opacity-60">
                  <option value="">— select —</option>
                  {f.options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
                </select>
              ) : (
                <Input type={f.type || "text"} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testid}-f-${f.key}`} className="mt-1" />
              )}
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} className="bg-primary hover:bg-red-700 w-full" data-testid={`${testid}-save`}>{busy ? "Saving…" : editing ? "Save changes" : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VehiclesSection({ canWrite }) {
  const { data, loading, setData } = useFetch(() => api.get("/fleet").then((r) => r.data));
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/erp/drivers").then((r) => setDrivers(r.data.drivers || [])).catch(() => {}); }, []);
  const reload = async () => setData((await api.get("/fleet")).data);
  if (loading) return <Spinner />;
  const s = data.summary;
  const driverOpts = drivers.map((d) => ({ value: d.id, label: d.name }));
  const fields = [
    { key: "reg_no", label: "Registration", required: true },
    { key: "type", label: "Type", type: "select", options: STATUS(["FTL", "LTL", "Container", "Refrigerated", "Flatbed", "Tanker"]), default: "FTL" },
    { key: "capacity_kg", label: "Capacity (kg)", type: "number", default: 14000 },
    { key: "capacity_cbm", label: "Capacity (cbm)", type: "number", default: 40 },
    { key: "branch", label: "Branch", type: "select", options: CITIES, default: "Johannesburg" },
    { key: "status", label: "Status", type: "select", options: STATUS(["Available", "In Transit", "Loading", "Maintenance"]), default: "Available" },
    { key: "driver_id", label: "Assign driver", type: "select", options: driverOpts },
    { key: "fuel_level", label: "Fuel %", type: "number", default: 100 },
    { key: "mileage_km", label: "Mileage (km)", type: "number", default: 0 },
  ];
  const save = async (payload) => {
    if (editing) await api.patch(`/erp/vehicles/${editing.id}`, payload); else await api.post("/erp/vehicles", payload);
    toast.success(editing ? "Vehicle updated" : "Vehicle added"); await reload();
  };
  const del = async (v) => { if (!window.confirm(`Delete vehicle ${v.reg_no}?`)) return; try { await api.delete(`/erp/vehicles/${v.id}`); toast.success("Vehicle deleted"); await reload(); } catch (e) { toast.error(efErr(e, "Delete failed")); } };

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard testid="fleet-total" label="Total Vehicles" value={s.total} icon={Truck} />
        <StatCard testid="fleet-available" label="Available" value={s.available} icon={CheckCircle2} accent="text-emerald-600" />
        <StatCard testid="fleet-transit" label="In Transit" value={s.in_transit} icon={Truck} accent="text-blue-600" />
        <StatCard testid="fleet-maint" label="Maintenance" value={s.maintenance} icon={Wrench} accent="text-orange-600" />
        <StatCard testid="fleet-util" label="Utilisation" value={`${s.utilization_pct}%`} icon={Gauge} accent="text-red-600" />
      </div>
      {canWrite && (
        <div className="flex justify-end mb-4">
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-primary hover:bg-red-700" data-testid="fleet-veh-add"><Plus size={16} className="mr-1.5" /> Add vehicle</Button>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-5">
        {data.vehicles.map((v) => (
          <div key={v.id} className="rounded-xl border border-border bg-card p-5" data-testid={`vehicle-${v.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading font-bold font-mono">{v.reg_no}</h3>
                <p className="text-sm text-muted-foreground">{v.type} · {(v.capacity_kg || 0).toLocaleString()} kg · {v.city || v.branch}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${VSTATUS[v.status] || ""}`}>{v.status}</span>
                {canWrite && (
                  <>
                    <Button variant="ghost" size="icon" onClick={() => { setEditing(v); setOpen(true); }} data-testid={`fleet-veh-edit-${v.id}`}><Pencil size={15} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(v)} data-testid={`fleet-veh-del-${v.id}`} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 size={15} /></Button>
                  </>
                )}
              </div>
            </div>
            {v.driver && <p className="text-sm mt-2">Driver: <b>{v.driver.name}</b> · ⭐ {v.driver.rating}</p>}
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="flex items-center gap-1.5 text-muted-foreground text-xs"><Fuel size={13} /> Fuel</div>
                <Progress value={v.fuel_level} className="h-1.5 mt-1" /><span className="text-xs">{v.fuel_level}%</span>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-muted-foreground text-xs"><Gauge size={13} /> Mileage</div>
                <p className="font-semibold">{(v.mileage_km || 0).toLocaleString()} km</p>
              </div>
              <div><span className="text-muted-foreground text-xs">Operating cost</span><p className="font-semibold">{zar(v.operating_cost_per_km)}/km</p></div>
              <div><span className="text-muted-foreground text-xs">Next service</span><p className="font-semibold">{(v.next_service_km || 0).toLocaleString()} km</p></div>
            </div>
            {v.maintenance_alerts && v.maintenance_alerts.length > 0 && (
              <div className="mt-3 rounded-lg bg-orange-50 dark:bg-orange-500/10 p-2.5">
                {v.maintenance_alerts.map((a, i) => (
                  <p key={i} className="text-xs text-orange-700 flex items-center gap-1.5"><AlertTriangle size={12} /> {a}</p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <EntityDialog open={open} setOpen={setOpen} title="Vehicle" testid="fleet-veh" editing={editing} fields={fields} onSave={save} />
    </div>
  );
}

function DriversSection({ canWrite }) {
  const { data, loading, setData } = useFetch(() => api.get("/erp/drivers").then((r) => r.data));
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/erp/vehicles").then((r) => setVehicles(r.data.vehicles || [])).catch(() => {}); }, []);
  const reload = async () => setData((await api.get("/erp/drivers")).data);
  if (loading) return <Spinner />;
  const rows = data.drivers || [];
  const vehOpts = vehicles.map((v) => ({ value: v.id, label: v.reg_no }));
  const fields = [
    { key: "name", label: "Full name", required: true, full: true },
    { key: "phone", label: "Phone" },
    { key: "license_no", label: "License number" },
    { key: "license_class", label: "License class", type: "select", options: STATUS(["EC", "EC1", "C1", "C"]), default: "EC" },
    { key: "branch", label: "Branch", type: "select", options: CITIES, default: "Johannesburg" },
    { key: "status", label: "Status", type: "select", options: STATUS(["Available", "On Duty", "Off Duty"]), default: "Available" },
    { key: "vehicle_id", label: "Assigned vehicle", type: "select", options: vehOpts },
  ];
  const save = async (payload) => {
    if (editing) await api.patch(`/erp/drivers/${editing.id}`, payload); else await api.post("/erp/drivers", payload);
    toast.success(editing ? "Driver updated" : "Driver added"); await reload();
  };
  const del = async (d) => { if (!window.confirm(`Delete driver ${d.name}?`)) return; try { await api.delete(`/erp/drivers/${d.id}`); toast.success("Driver deleted"); await reload(); } catch (e) { toast.error(efErr(e, "Delete failed")); } };
  return (
    <div className="space-y-4">
      {canWrite && <div className="flex justify-end"><Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-primary hover:bg-red-700" data-testid="fleet-drv-add"><Plus size={16} className="mr-1.5" /> Add driver</Button></div>}
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="p-4">Name</th><th className="p-4">Phone</th><th className="p-4">License</th><th className="p-4">Branch</th><th className="p-4">Status</th><th className="p-4">Rating</th>{canWrite && <th className="p-4 text-right">Actions</th>}</tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && <tr><td colSpan={canWrite ? 7 : 6} className="p-6 text-muted-foreground">No drivers yet.</td></tr>}
            {rows.map((d) => (
              <tr key={d.id} className="hover:bg-muted/40" data-testid={`fleet-drv-row-${d.id}`}>
                <td className="p-4 font-semibold flex items-center gap-2"><UserCog size={15} className="text-muted-foreground" /> {d.name}</td>
                <td className="p-4 text-muted-foreground">{d.phone || "—"}</td>
                <td className="p-4">{d.license_no || "—"} <span className="text-xs text-muted-foreground">{d.license_class}</span></td>
                <td className="p-4">{d.branch}</td>
                <td className="p-4">{d.status}</td>
                <td className="p-4">⭐ {d.rating ?? "—"}</td>
                {canWrite && (
                  <td className="p-4 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => { setEditing(d); setOpen(true); }} data-testid={`fleet-drv-edit-${d.id}`}><Pencil size={16} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(d)} data-testid={`fleet-drv-del-${d.id}`} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 size={16} /></Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <EntityDialog open={open} setOpen={setOpen} title="Driver" testid="fleet-drv" editing={editing} fields={fields} onSave={save} />
    </div>
  );
}

function MaintenanceSection({ canWrite }) {
  const { data, loading, setData } = useFetch(() => api.get("/erp/maintenance").then((r) => r.data));
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/erp/vehicles").then((r) => setVehicles(r.data.vehicles || [])).catch(() => {}); }, []);
  const reload = async () => setData((await api.get("/erp/maintenance")).data);
  if (loading) return <Spinner />;
  const rows = data.maintenance || [];
  const vehOpts = vehicles.map((v) => ({ value: v.id, label: `${v.reg_no} · ${v.branch}` }));
  const fields = [
    { key: "vehicle_id", label: "Vehicle", type: "select", options: vehOpts, required: true, lockOnEdit: true },
    { key: "type", label: "Type", type: "select", options: STATUS(["Service", "Repair", "Inspection", "Tyres", "Bodywork", "Other"]), default: "Service" },
    { key: "description", label: "Description", full: true },
    { key: "provider", label: "Service provider" },
    { key: "cost_zar", label: "Cost (ZAR)", type: "number", default: 0 },
    { key: "odometer_km", label: "Odometer (km)", type: "number", default: 0 },
    { key: "service_date", label: "Service date", type: "date" },
    { key: "next_service_km", label: "Next service at (km)", type: "number" },
    { key: "status", label: "Status", type: "select", options: STATUS(["Scheduled", "In Progress", "Completed", "Cancelled"]), default: "Scheduled" },
  ];
  const save = async (payload) => {
    if (editing) await api.patch(`/erp/maintenance/${editing.id}`, payload); else await api.post("/erp/maintenance", payload);
    toast.success(editing ? "Maintenance updated" : "Maintenance logged"); await reload();
  };
  const del = async (m) => { if (!window.confirm("Delete this maintenance record?")) return; try { await api.delete(`/erp/maintenance/${m.id}`); toast.success("Deleted"); await reload(); } catch (e) { toast.error(efErr(e, "Delete failed")); } };
  const badge = (st) => st === "Completed" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15" : st === "In Progress" ? "bg-orange-100 text-orange-700 dark:bg-orange-500/15" : st === "Cancelled" ? "bg-slate-100 text-slate-600" : "bg-blue-100 text-blue-700 dark:bg-blue-500/15";
  return (
    <div className="space-y-4">
      {canWrite && <div className="flex justify-end"><Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-primary hover:bg-red-700" data-testid="fleet-mnt-add"><Plus size={16} className="mr-1.5" /> Log maintenance</Button></div>}
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="p-4">Date</th><th className="p-4">Vehicle</th><th className="p-4">Type</th><th className="p-4">Description</th><th className="p-4 text-right">Cost</th><th className="p-4">Status</th>{canWrite && <th className="p-4 text-right">Actions</th>}</tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && <tr><td colSpan={canWrite ? 7 : 6} className="p-6 text-muted-foreground">No maintenance records yet.</td></tr>}
            {rows.map((m) => (
              <tr key={m.id} className="hover:bg-muted/40" data-testid={`fleet-mnt-row-${m.id}`}>
                <td className="p-4">{(m.service_date || "").slice(0, 10)}</td>
                <td className="p-4 font-mono font-semibold">{m.vehicle_reg}</td>
                <td className="p-4 flex items-center gap-1.5"><Wrench size={14} className="text-muted-foreground" /> {m.type}</td>
                <td className="p-4 text-muted-foreground max-w-xs truncate">{m.description || "—"}{m.provider ? ` · ${m.provider}` : ""}</td>
                <td className="p-4 text-right">{money(m.cost_zar)}</td>
                <td className="p-4"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badge(m.status)}`}>{m.status}</span></td>
                {canWrite && (
                  <td className="p-4 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => { setEditing(m); setOpen(true); }} data-testid={`fleet-mnt-edit-${m.id}`}><Pencil size={16} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(m)} data-testid={`fleet-mnt-del-${m.id}`} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 size={16} /></Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <EntityDialog open={open} setOpen={setOpen} title="Maintenance" testid="fleet-mnt" editing={editing} fields={fields} onSave={save} />
    </div>
  );
}

export default function Fleet() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  return (
    <div className="pb-20">
      <PageHeader title="Fleet Management" subtitle="Vehicles, drivers, maintenance and utilisation" />
      <Tabs defaultValue="vehicles">
        <TabsList>
          <TabsTrigger value="vehicles" data-testid="fleet-tab-vehicles">Vehicles</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="fleet-tab-drivers">Drivers</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="fleet-tab-maintenance">Maintenance</TabsTrigger>
        </TabsList>
        <TabsContent value="vehicles"><VehiclesSection canWrite={canWrite} /></TabsContent>
        <TabsContent value="drivers"><DriversSection canWrite={canWrite} /></TabsContent>
        <TabsContent value="maintenance"><MaintenanceSection canWrite={canWrite} /></TabsContent>
      </Tabs>
    </div>
  );
}
